import { RiFileLine } from '@remixicon/react';

import { Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { Text } from '@backstage/ui';

import { extractLastNameComponent } from '@spotify/confidence-core-react';
import { useCollaborativeDocumentRef } from '@spotify/confidence-editor-react';
import {
  type ToolGroupEntry,
  createToolExtension,
} from '@spotify/plugin-confidence-chat-react';

import { confidenceChatPlugin } from '../plugin';
import { collaborativeDocumentRouteRef } from '../routes';

const DOCUMENT_TOOL_SUFFIXES = [
  'read_document',
  'write_document',
  'edit_document',
];

function getDocumentName(input: unknown): string | undefined {
  if (input && typeof input === 'object' && 'name' in input) {
    const name = (input as { name?: string }).name;
    return typeof name === 'string' && name.length > 0 ? name : undefined;
  }
  return undefined;
}

function getInputTitle(input: unknown): string | undefined {
  if (input && typeof input === 'object' && 'title' in input) {
    const title = (input as { title?: string }).title;
    return typeof title === 'string' && title.length > 0 ? title : undefined;
  }
  return undefined;
}

function getActionVerb(
  tool: string,
  { completed }: { completed: boolean },
): string {
  if (tool.endsWith('read_document')) {
    return completed ? 'Read' : 'Reading';
  }
  if (tool.endsWith('edit_document')) {
    return completed ? 'Edited' : 'Editing';
  }
  return completed ? 'Wrote' : 'Writing';
}

const DocumentToolLabel = ({ entry }: { entry: ToolGroupEntry }) => {
  const documentName = getDocumentName(entry.input);
  const { collaborativeDocument } = useCollaborativeDocumentRef({
    name: documentName ?? '',
    skip: !documentName,
  });
  const collaborativeDocumentRoute = useRouteRef(collaborativeDocumentRouteRef);

  const completed = Boolean(entry.result);
  const verb = getActionVerb(entry.tool, { completed });
  const title =
    collaborativeDocument?.title ??
    entry.displayName ??
    getInputTitle(entry.input) ??
    'Untitled document';

  const documentId = extractLastNameComponent(documentName);
  const href = documentId
    ? collaborativeDocumentRoute?.({ id: documentId })
    : undefined;

  return (
    <Text variant="body-small" color="secondary">
      {verb}{' '}
      {href ? (
        <Link to={href} style={{ color: 'inherit' }}>
          {title}
        </Link>
      ) : (
        title
      )}
    </Text>
  );
};

export const DocumentToolExtension = confidenceChatPlugin.provide(
  createToolExtension({
    id: 'document-operation',
    predicate: tool =>
      DOCUMENT_TOOL_SUFFIXES.some(suffix => tool.endsWith(suffix)),
    icon: RiFileLine,
    display: 'grouped',
    renderLabel: entry => <DocumentToolLabel entry={entry} />,
    render: () => null,
  }),
);
